import React, { useEffect, useState } from 'react';
import { RichText, Image, Link, SectionHeading, MaskedImage, Search } from '@/components';
import type { PortableTextBlock as PortableTextBlockType, ImageAsset } from '@sanity/types';
import { LinkInterface } from '@/types';
import { bgColorClassname } from '@/utils';
import { Button } from '@/components/ui/button';
import { navigate } from 'gatsby';
import { useLocation } from '@reach/router';
interface ImageWithMask {
  alt?: string;
  image: {
    asset: ImageAsset;
  };
  mask: string;
}

interface Props {
  backgroundColor: {
    label: string;
    value: string;
  };
  heading: string;
  includeSubheading: boolean;
  subheading?: string;
  blurb?: PortableTextBlockType;
  description?: string;
  includeSquiggle: boolean;
  isBiggerHero: boolean;
  isCompactHero: boolean;
  heroImage?: ImageWithMask;
  includeCta: boolean;
  cta?: LinkInterface | undefined;
  doodadLayout?: string | undefined;
  backgroundElementLeft?: string | undefined;
  backgroundElementRight?: string | undefined;
  className?: string | undefined;
  blurbClassName?: string | undefined;
  isSearchPageHero: boolean;
  includeCtas: boolean;
  ctas?: LinkInterface[] | undefined;
}

const Hero: React.FC<Props> = ({
  backgroundColor,
  heading,
  includeSubheading,
  subheading,
  blurb,
  description,
  includeSquiggle,
  isBiggerHero,
  isCompactHero,
  heroImage,
  includeCta,
  cta,
  doodadLayout,
  backgroundElementLeft,
  backgroundElementRight,
  className,
  blurbClassName,
  isSearchPageHero,
  includeCtas,
  ctas,
}) => {
  const HeroImageComponent = () => {
    if (!heroImage) return null;

    if (heroImage.mask != 'no-mask') {
      return (
        <MaskedImage
          image={heroImage?.image}
          mask={heroImage.mask}
          className='w-full'
          alt={heroImage?.alt || heading}
          width={660}
        />
      );
    }

    return (
      <Image
        className='w-full rounded-[20px]'
        image={heroImage?.image}
        alt={heroImage?.alt || heading}
        width={660}
      />
    );
  };

  const sectionBgElementLeft = () => {
    if (!backgroundElementLeft) {
      return '';
    }

    const commonClasses =
      'relative z-10 before:bg-no-repeat before:absolute lg:before:h-full before:top-0 before:right-0 before:bottom-0 before:z-[-1]';

    switch (backgroundElementLeft) {
      case 'circle_stack':
        return `${commonClasses} before:bg-element-circle-stack before:-left-7 before:bg-left-top before:bg-[length:278px]`;
      case 'ovals':
        return `${commonClasses} before:bg-element-ovals before:-left-[53px] before:bg-left before:bg-[length:297px]`;
      case 'half_circles_left':
        return `${commonClasses} before:bg-element-half-circles-left before:left-0 before:bg-left before:bg-[length:333px]`;
      case 'half_circles_right':
        return `${commonClasses} before:bg-element-half-circles-right before:left-0 before:bg-left before:bg-[length:333px]`;
      default:
        return '';
    }
  };

  const sectionBgElementRight = () => {
    if (!backgroundElementRight) {
      return '';
    }

    const commonClasses =
      'relative z-10 after:bg-no-repeat after:absolute lg:after:h-full after:top-0 after:left-0 after:bottom-0 after:z-[-1]';

    switch (backgroundElementRight) {
      case 'circle_stack':
        return `${commonClasses} after:bg-element-circle-stack after:-right-7 after:bg-right-top after:bg-[length:278px]`;
      case 'ovals':
        return `${commonClasses} after:bg-element-ovals after:-right-9 after:bg-right after:bg-[length:297px]`;
      case 'half_circles_left':
        return `${commonClasses} after:bg-element-half-circles-left after:right-0 after:bg-right after:bg-[length:333px]`;
      case 'half_circles_right':
        return `${commonClasses} after:bg-element-half-circles-right after:right-0 after:bg-right after:bg-[length:333px]`;
      default:
        return '';
    }
  };

  function getImageOvelyProps(): string {
    switch (doodadLayout) {
      case 'layout1':
        return 'after:bg-layout1 lg:after:-top-8 lg:after:-right-12 lg:after:-bottom-9 lg:after:-left-12';
      case 'layout2':
        return 'after:bg-layout2 lg:after:top-0 lg:after:-right-7 lg:after:bottom-5 lg:after:-left-3';
      case 'layout3':
        return 'after:bg-layout3 lg:after:top-3 lg:after:right-6 lg:after:bottom-2.5 lg:after:-left-12';
      case 'layout4':
        return 'after:bg-layout4 lg:after:top-0 lg:after:-right-10 lg:after:bottom-0 lg:after:-left-12';
      case 'layout5':
        return 'after:bg-layout5 lg:after:-top-11 lg:after:-right-9 lg:after:-bottom-5 lg:after:-left-9';
      case 'layout6':
        return 'after:bg-layout6 lg:after:top-9 lg:after:-right-7 lg:after:-bottom-0.5 lg:after:-left-9';
      case 'layout7':
        return 'after:bg-layout7 lg:after:top-0 lg:after:-right-9 lg:after:bottom-2 lg:after:-left-12';
      case 'layout8':
        return 'after:bg-layout8 lg:after:top-0 lg:after:-right-8 lg:after:-bottom-5 lg:after:-left-0.5';
      case 'layout9':
        return 'after:bg-layout9 lg:after:top-0 lg:after:-right-16 lg:after:bottom-6 lg:after:left-0';
      default:
        return '';
    }
  }

  function getAfterBgClassByColor(): string {
    switch (backgroundColor.label) {
      case 'teal-200':
        return 'after:bg-gradient-to-b from-teal-200 to-teal-300';
      case 'teal-300':
        return 'after:bg-gradient-to-b from-teal-300 to-teal-400';
      case 'teal-400':
        return 'after:bg-gradient-to-b from-teal-400 to-teal-500';
      case 'teal-500':
        return 'after:bg-gradient-to-b from-teal-500 to-teal-600';
      case 'green-200':
        return 'after:bg-gradient-to-b from-green-200 to-green-300';
      case 'green-500':
        return 'after:bg-gradient-to-b from-green-500 to-green-600';
      case 'yellow-400':
        return 'after:bg-gradient-to-b from-yellow-400 to-yellow-500';
      case 'yellow-500':
        return 'after:bg-gradient-to-b from-yellow-500 to-yellow-600';
      case 'pink-100':
        return 'after:bg-gradient-to-b from-pink-100 to-pink-200';
      case 'pink-500':
        return 'after:bg-gradient-to-b from-pink-500 to-pink-600';
      case 'sand-500':
        return 'after:bg-gradient-to-b from-sand-500 to-sand-600';
      case 'blue-500':
        return 'after:bg-gradient-to-b from-blue-500 to-blue-600';
      case 'blue-800':
        return 'after:bg-gradient-to-b from-blue-800 to-blue-900';
      default:
        return '';
    }
  }

  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [shouldFetch, setShouldFetch] = useState<boolean>(false);

  const searchQueryFromURL = new URLSearchParams(location.search).get('term') || '';

  const handleSearch = () => {
    if (searchTerm.trim()) {
      navigate(`/search?term=${encodeURIComponent(searchTerm)}`);
      setShouldFetch(true);
    }
  };

  useEffect(() => {
    if (searchQueryFromURL && searchQueryFromURL !== searchTerm) {
      setSearchTerm(searchQueryFromURL);
      setShouldFetch(true);
    }
  }, [searchQueryFromURL]);

  return (
    <>
      <div
        className={`hero-main relative z-20 ${getAfterBgClassByColor()} after:absolute after:-bottom-5 after:left-0 after:right-0 after:z-10 after:h-9`}
      >
        <div
          className={`overflow-hidden ${sectionBgElementLeft()} ${sectionBgElementRight()} ${bgColorClassname(backgroundColor)} ${backgroundElementLeft ? `after:hidden md:after:block` : ``} `}
        >
          <div className='hero-container mx-auto max-w-screen-xl p-5 pb-10 lg:p-12'>
            <div className='hero-wrapper'>
              <div className='flex flex-wrap items-center gap-5 md:flex-nowrap md:gap-12 lg:min-h-[442px]'>
                <div
                  className={`w-full max-w-[612px] md:min-w-[340px] md:flex-1 lg:min-w-[380px] xl:min-w-[480px] ${!heroImage ? 'mx-auto my-20 text-center lg:my-0' : 'mt-12 md:mt-0'} ${className ? className : ''}`}
                >
                  <SectionHeading
                    title={heading}
                    isShowCurveLine={includeSquiggle}
                    size={isBiggerHero ? 'lg' : isCompactHero ? 'xs' : 'sm'}
                    hasSubHeading={includeSubheading}
                    subHeading={subheading}
                    justifyContent={heroImage ? 'justify-start' : 'justify-center'}
                  />
                  {blurb && (
                    <RichText
                      className={`mt-3 text-xl font-normal leading-8 text-black md:text-2xl ${blurbClassName}`}
                      value={blurb}
                    />
                  )}
                  {description && (
                    <p
                      className={`mt-3 text-xl font-normal leading-7 text-black md:text-2xl ${blurbClassName}`}
                    >
                      {description}
                    </p>
                  )}
                  {isSearchPageHero && (
                    <div className='mt-7 flex w-full max-w-[700px] flex-wrap gap-y-3 sm:flex-nowrap sm:gap-x-2'>
                      <input
                        type='search'
                        name='search'
                        value={searchTerm}
                        onChange={(e) => {
                          setSearchTerm(e.target.value);
                          setShouldFetch(false);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleSearch();
                          }
                        }}
                        className='bg-icon-search block w-full rounded-full border-0 bg-[left_28px_center] bg-no-repeat py-4 pl-16 pr-7 text-base font-normal leading-5 text-gray-900 shadow-[0_0_4px_0_rgb(182,183,200,0.5)] outline-0 ring-0 placeholder:font-normal placeholder:text-gray-400 focus:ring-0'
                        placeholder='Press release, Media coverage, or Others....'
                      />
                      <Button
                        variant='default'
                        size='lg'
                        className='mx-auto w-full max-w-60 sm:w-auto'
                        onClick={handleSearch}
                      >
                        search
                      </Button>
                    </div>
                  )}
                  {includeCta && cta && (
                    <>
                      <Link
                        {...cta}
                        variant='default'
                        size={isBiggerHero ? 'lg' : 'sm'}
                        className={`mt-5 ${isBiggerHero ? 'hidden md:inline-block' : 'inline-block'}`}
                      >
                        {cta?.label}
                      </Link>
                      <Link
                        {...cta}
                        variant='default'
                        size='sm'
                        className={`mt-5 ${isBiggerHero ? 'inline-block md:hidden' : 'hidden'}`}
                      >
                        {cta?.label}
                      </Link>
                    </>
                  )}
                  {includeCtas && ctas && ctas?.length > 0 && (
                    <div className='mt-8 flex flex-wrap justify-center gap-4'>
                      {ctas.map((cta, index) => (
                        <Link
                          key={index}
                          {...cta}
                          variant={cta.type === 'primary' ? 'default' : 'outline'}
                          size='sm'
                        >
                          {cta.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
                {heroImage && (
                  <div className='w-full'>
                    <div
                      className={`hero-image-wrapper md:mr-12 md:py-16 lg:mr-16 xl:mr-0 ${doodadLayout ? `relative after:absolute after:inset-0 after:z-[10] after:bg-contain after:bg-center after:bg-no-repeat ${getImageOvelyProps()}` : ''}`}
                    >
                      <HeroImageComponent />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {isSearchPageHero && (
        <Search
          searchTerm={searchTerm}
          shouldFetch={shouldFetch}
        />
      )}
    </>
  );
};

export default Hero;
